/**
 * Error Boundary Component for FlowRide Frontend
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 */

import { IonIcon, IonPage } from "@ionic/react";
import { arrowBackCircle } from "ionicons/icons";
import React, { useCallback, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { mapStackTrace } from "sourcemapped-stacktrace";
import { ErrorReportService } from "../../services/ErrorReportService";
import { buildEphemeralSlicesSnapshot } from "../../store/ephemeral";
import { buildPersistedSlicesSnapshot } from "../../store/persistence";
import { isActionableGlobalErrorValue, isBenignWindowErrorEvent } from "../../tools/benignWindowErrors.tools";

/** State managed by the ErrorBoundary */
interface ErrorBoundaryState {
    "hasError": boolean;
    "message":  string;
    "stack"?:   string;
    "name"?:    string;
}

/** Props for the ErrorBoundary */
interface ErrorBoundaryProps { "children": React.ReactNode }

/** Type guard to check if object has property */
function hasProperty<T extends object, K extends PropertyKey>(
    obj: T,
    key: K,
): obj is T & Record<K, unknown> {
    return key in obj;
}

/** Interface for error-like objects */
interface ErrorLikeObject {
    "message"?:  unknown;
    "stack"?:    unknown;
    "name"?:     unknown;
    "response"?: { "data"?: unknown };
}

/**
 * mapStackTrace is provided as a callback.
 * We wrap it in a Promise to be able to use it with async/await.
 */
async function mapStackTraceAsync( stack: string ): Promise<string> {
    return await new Promise( ( resolve ) => {
        mapStackTrace( stack, ( mappedStack: string[] ) => {
            // Return the reconstructed stack as a string
            resolve( mappedStack.join( "\n" ) );
        } );
    } );
}

/**
 * Asynchronous utility function that normalizes the error AND transforms the stack via mapStackTrace.
 */
async function normalizeErrorAsync( error: unknown ): Promise<{
    "message": string;
    "stack"?:  string;
    "name"?:   string;
}> {
    let message = "";
    let stack: string | undefined;
    let name: string | undefined;

    if ( error instanceof Error ) {
        message = error.message;
        name = error.name;
        if ( error.stack != null ) {
            stack = await mapStackTraceAsync( error.stack );
        }
    } else if ( typeof error === "string" ) {
        message = error;
        name = "Error";
    } else if ( typeof error === "object" && error !== null ) {
        const errObj = error as ErrorLikeObject;

        // Vérification explicite de la présence d'une réponse avec data
        if ( hasProperty( errObj, "response" ) && errObj.response !== null && typeof errObj.response === "object" && hasProperty( errObj.response, "data" ) && errObj.response.data !== null && errObj.response.data !== undefined ) {
            const responseData = errObj.response.data;
            if ( typeof responseData === "object" && responseData !== null ) {
                if ( hasProperty( responseData, "message" ) && typeof responseData.message === "string" ) {
                    message = responseData.message;
                } else if ( hasProperty( responseData, "messsage" ) && typeof responseData.messsage === "string" ) {
                    // parfois on voit des typos "messsage"
                    message = responseData.messsage;
                }
            } else if ( typeof responseData === "string" ) {
                message = responseData;
            }
        }

        // Si aucun message n'a été trouvé via response, on vérifie la propriété message
        if ( message === "" && hasProperty( errObj, "message" ) && typeof errObj.message === "string" ) {
            message = errObj.message;
        }

        // En dernier recours, on essaie de convertir l'erreur en JSON
        if ( message === "" ) {
            try {
                message = JSON.stringify( error );
            } catch {
                message = "Une erreur inconnue est survenue";
            }
        }

        // Vérification explicite de la stack
        if ( hasProperty( errObj, "stack" ) && typeof errObj.stack === "string" ) {
            stack = await mapStackTraceAsync( errObj.stack );
        }

        // Vérification explicite du nom de l'erreur
        if ( hasProperty( errObj, "name" ) && typeof errObj.name === "string" ) {
            name = errObj.name;
        } else {
            name = "Error";
        }
    } else {
        // Cas inconnu
        message = "Une erreur inconnue est survenue";
        name = "Error";
    }

    return {
        message,
        stack,
        name,
    };
}

/**
 * Tronque les URLs dans une stack trace pour ne conserver que la partie à partir de "/src/".
 */
function shortenStackTrace( stack: string ): string {
    return stack.
        split( "\n" ).
        map( ( line ) => line.replace( /^(.*?)(\/src\/.*)/, "$2" ) ).
        join( "\n" );
}

/**
 * Composant de fallback affiché en cas d'erreur.
 */
const FallbackUI: React.FC<{
    "error":             ErrorBoundaryState;
    "onReset":           () => void;
    "onCaptureContexts": ( ephemeralSlicesSnapshot: Record<string, unknown>, persistedSlicesSnapshot: Record<string, unknown> ) => void;
}> = ( { error, onReset, onCaptureContexts } ) => {
    const history = useHistory();
    const location = useLocation();

    console.error( "Erreur capturée (affichée dans le fallback) :", error );

    const memoizedCaptureContexts = useCallback( () => {
        onCaptureContexts( buildEphemeralSlicesSnapshot(), buildPersistedSlicesSnapshot() );
    }, [ onCaptureContexts ] );

    useEffect( () => {
        // Capturer les contextes pour l'envoi d'erreur (une seule fois)
        memoizedCaptureContexts();
    }, [ memoizedCaptureContexts ] );

    return (
        <IonPage className="bg-white">
            <div className="flex flex-col min-h-screen bg-white h-screen">
                <div className="flex-grow flex flex-col items-center justify-start p-5">
                    <p className="text-ionic-light bg-ionic-primary p-4 rounded-lg text-base my-5 whitespace-pre-wrap min-w-32 text-center">
                        {error.message}
                    </p>
                    {error.stack != null &&
                      <pre className="text-left text-ionic-primary bg-ionic-light h-1/2 p-4 rounded-lg w-10/12 whitespace-pre-wrap">
                          {shortenStackTrace( error.stack )}
                      </pre>}
                    <div
                        className="bg-ionic-secondary text-white py-2 px-4 rounded flex items-center cursor-pointer mt-5"
                        onClick={() => {
                            onReset();
                            history.push( history.location.pathname, location.state );
                        }}
                    >
                        <IonIcon
                            className="mr-2"
                            icon={arrowBackCircle}
                        />
                        Retour
                    </div>
                </div>
            </div>
        </IonPage>
    );
};

/**
 * ErrorBoundary qui capture :
 * - les erreurs de rendu via getDerivedStateFromError / componentDidCatch,
 * - les erreurs globales (window.addEventListener 'error'),
 * - les rejets de promesses non gérés (window.addEventListener 'unhandledrejection').
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {

    private ephemeralSlicesSnapshot: Record<string, unknown> | null = null;

    private persistedSlicesSnapshot: Record<string, unknown> | null = null;

    private errorReportSent: boolean = false;

    private pendingError: Error | null = null;

    private pendingAdditionalContext?: Record<string, unknown>;

    constructor( props: ErrorBoundaryProps ) {
        super( props );
        this.state = {
            "hasError": false,
            "message":  "",
            "stack":    undefined,
            "name":     "",
        };
    }

    componentDidMount() {
        window.addEventListener( "error", this.handleGlobalError );
        window.addEventListener( "unhandledrejection", this.handleUnhandledRejection );
    }

    componentWillUnmount() {
        window.removeEventListener( "error", this.handleGlobalError );
        window.removeEventListener( "unhandledrejection", this.handleUnhandledRejection );
    }

    /**
     * Capture les contextes depuis les providers React
     */
    private captureContexts() {
        try {
            /*
             * Cette méthode sera appelée depuis le FallbackUI qui a accès aux contextes
             * Les contextes seront passés via les props ou une autre méthode
             */
        } catch( error ) {
            console.warn( "Failed to capture contexts:", error );
        }
    }

    /**
     * Méthode pour capturer les contextes depuis le FallbackUI
     */
    private readonly handleCaptureContexts = ( ephemeralSlicesSnapshot: Record<string, unknown>, persistedSlicesSnapshot: Record<string, unknown> ) => {
        this.ephemeralSlicesSnapshot = ephemeralSlicesSnapshot;
        this.persistedSlicesSnapshot = persistedSlicesSnapshot;

        // Envoyer un rapport d'erreur avec les contextes maintenant qu'ils sont disponibles
        if ( !this.errorReportSent && this.pendingError ) {
            this.errorReportSent = true;
            void this.sendErrorReport( this.pendingError, this.pendingAdditionalContext );
        }
    };

    /**
     * Envoie un rapport d'erreur avec les contextes
     */
    private async sendErrorReport( error: Error, additionalContext?: Record<string, unknown> ) {
        try {
            await ErrorReportService.sendErrorReport( error, {
                "ephemeralSlicesSnapshot": this.ephemeralSlicesSnapshot ?? undefined,
                "persistedSlicesSnapshot": this.persistedSlicesSnapshot ?? undefined,
                ...additionalContext,
            } );
        } catch( reportError ) {
            console.error( "Failed to send error report:", reportError );
        }
    }

    /**
     * Cette méthode est synchrone et permet à React de basculer
     * immédiatement vers le fallback. On ne peut pas y faire d'appel async.
     */
    static getDerivedStateFromError(): ErrorBoundaryState {
        // On déclenche simplement l'état d'erreur ; la stack sera ensuite affinée dans componentDidCatch
        return {
            "hasError": true,
            "message":  "",
            "stack":    undefined,
            "name":     "",
        };
    }

    /**
     * Après que React ait basculé l'UI en fallback, on peut faire
     * l'appel async pour récupérer la stack reconstituée et mettre à jour le state.
     */
    componentDidCatch( error: unknown, errorInfo: React.ErrorInfo ) {
        console.error( "Erreur capturée dans componentDidCatch :", error, errorInfo );

        // On mappe l'erreur de façon asynchrone
        void normalizeErrorAsync( error ).then( ( { message, stack, name } ) => {
            this.setState( {
                "hasError": true,
                message,
                stack,
                name,
            } );
        } );

        // Stocker l'erreur pour l'envoyer avec les contextes
        if ( error instanceof Error ) {
            this.pendingError = error;
            this.pendingAdditionalContext = { "errorInfo": errorInfo.componentStack };
        }
    }

    /**
     * Capture explicite des erreurs globales synchrones
     */
    private readonly handleGlobalError = ( event: ErrorEvent ): boolean => {
        if ( isBenignWindowErrorEvent( event ) ) {
            return true;
        }

        const error: unknown = event.error;
        if ( !isActionableGlobalErrorValue( error ) ) {
            if ( __IS_DEV__ && ( event.message ?? "" ).length > 0 ) {
                console.warn(
                    "[ErrorBoundary] Ignored global error without actionable Error instance:",
                    event.message,
                    event,
                );
            }

            return true;
        }

        event.preventDefault();

        void normalizeErrorAsync( error ).then( ( { message, stack, name } ) => {
            console.error( "Erreur globale capturée :", error );
            this.setState( {
                "hasError": true,
                message,
                stack,
                name,
            } );
        } );

        if ( error instanceof Error ) {
            this.pendingError = error;
            this.pendingAdditionalContext = undefined;
        }

        return true;
    };

    /**
     * Capture explicite des rejets de promesses non gérés (asynchrones)
     */
    private readonly handleUnhandledRejection = ( event: PromiseRejectionEvent ) => {
        const reason: unknown = event.reason;
        if ( !isActionableGlobalErrorValue( reason ) ) {
            return;
        }

        event.preventDefault();

        void normalizeErrorAsync( reason ).then( ( { message, stack, name } ) => {
            console.error( "Rejet non géré capturé :", reason );
            this.setState( {
                "hasError": true,
                message,
                stack,
                name,
            } );
        } );

        if ( reason instanceof Error ) {
            this.pendingError = reason;
            this.pendingAdditionalContext = undefined;
        }
    };

    /**
     * Permet de réinitialiser l'état d'erreur et de retenter le rendu normal.
     */
    resetError = () => {
        this.setState( {
            "hasError": false,
            "message":  "",
            "stack":    undefined,
            "name":     "",
        } );
        this.errorReportSent = false;
        this.pendingError = null;
        this.pendingAdditionalContext = undefined;
    };

    render() {
        return this.state.hasError
            ? <FallbackUI
                    error={this.state}
                    onCaptureContexts={this.handleCaptureContexts}
                    onReset={this.resetError}
              />
            : this.props.children;
    }

}

export default ErrorBoundary;
